import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CanvasCardFrame, { CanvasCardDragSharedProps } from './CanvasCardFrame';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllPropertyDefinitions, selectAllRepositories, selectAllRepositoryEntries, selectAllEntryProperties } from '../containers/contentSlice';
import { populateRepositoryEntriesForTable } from '../common/util';
import { filterOfflineRowsByArchivedAndDataFilter } from '../common/offlineDataFilter';
import {
	useGetRepositoryByIdQuery,
	useGetRepositoryEntriesByFilterQuery,
} from '../services/repositoryEntries';
import { Bar, BarChart, Line, LineChart, XAxis, YAxis } from 'recharts';
import {
	buildCanvasChartSeries,
	isChartDateLikePropertyType,
	resolveChartTargetReference,
	type ChartPresentationVariant,
	type ChartTimeScale,
} from '../chart/canvasChartData';
import ChartTargetReferenceLines from '../chart/ChartTargetReferenceLines';
import {
	parseResolvedChartDataFilterBoundsForSlug,
	resolveChartWhereForApi,
} from '../chart/resolveChartWhereForApi';
import ChartContributionHeatmap from '../chart/ChartContributionHeatmap';

export interface CanvasChartCardProps extends CanvasCardDragSharedProps {
	label: string;
	onMenuClick: (e: React.MouseEvent) => void;
	setMenuAnchorEl: (el: HTMLElement | null) => void;
	repositoryId: string;
	where?: string | null;
	/** When false, chart data excludes archived repository entries. */
	includeArchived?: boolean;
	metric?: string;
	xAxis?: string;
	xAxisTimeScale?: 'D' | 'W' | 'M' | null;
	variant?: ChartPresentationVariant;
	targetA?: number | null;
	targetB?: number | null;
}

function normalizeTimeScale(raw: string | null | undefined): ChartTimeScale | null {
	const t = (raw ?? '').trim();
	if (t === 'D' || t === 'W' || t === 'M') {
		return t;
	}
	return null;
}

function CanvasChartCardBase({
	label,
	position,
	isSelected,
	scale,
	collectionColor,
	isPositionLocked,
	onPositionChange,
	constrainPosition,
	onDragStart,
	onDragActivate,
	onDragMove,
	onDragEnd,
	cardNodeRef,
	onFocus,
	onBlur,
	onSelect,
	repositoryId,
	where,
	includeArchived = true,
	metric,
	xAxis,
	xAxisTimeScale,
	variant: variantProp,
	targetA,
	targetB,
}: CanvasChartCardProps) {
	const theme = useTheme();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allRepositories = useAppSelector(selectAllRepositories);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);
	const metricKey = (metric ?? '').trim();
	const xAxisKey = (xAxis ?? '').trim();
	const vr = (variantProp ?? '').trim();
	const presentationVariant: ChartPresentationVariant =
		vr === 'bar' || vr === 'heatmap' ? vr : 'line';
	const timeScaleFromChart = normalizeTimeScale(xAxisTimeScale ?? undefined);

	const { data: repositoryById } = useGetRepositoryByIdQuery(repositoryId, {
		skip: !isAuthenticated || !repositoryId,
	});
	const xAxisPropertyType = useMemo(() => {
		if (!xAxisKey) {
			return undefined;
		}
		if (isAuthenticated) {
			return repositoryById?.propertyDefinitions?.find((def) => def.slug === xAxisKey)?.type;
		}
		return allPropertyDefinitions.find((def) => def.parentId === repositoryId && def.slug === xAxisKey)?.type;
	}, [allPropertyDefinitions, isAuthenticated, repositoryById?.propertyDefinitions, repositoryId, xAxisKey]);
	const dateLikeAxis = isChartDateLikePropertyType(xAxisPropertyType);
	const heatmapMode = presentationVariant === 'heatmap' && dateLikeAxis;
	const useTimeBucketsForSeries = heatmapMode || Boolean(timeScaleFromChart && dateLikeAxis);
	const timeScaleForSeries: ChartTimeScale | null = heatmapMode ? 'D' : timeScaleFromChart;

	const resolvedDataFilter = useMemo(() => resolveChartWhereForApi(where), [where]);
	const timeBucketRangeFromFilter = useMemo(() => {
		if (!useTimeBucketsForSeries || !resolvedDataFilter || !xAxisKey) {
			return null;
		}
		return parseResolvedChartDataFilterBoundsForSlug(resolvedDataFilter, xAxisKey);
	}, [useTimeBucketsForSeries, resolvedDataFilter, xAxisKey]);

	const { data, isFetching } = useGetRepositoryEntriesByFilterQuery({
		repositoryId,
		dataFilter: resolvedDataFilter,
		includeArchived,
		sortBy: useTimeBucketsForSeries ? undefined : (xAxisKey || undefined),
		sortDir: 'asc',
	}, {
		skip: !isAuthenticated || !repositoryId,
	});
	const offlineRepositoryEntries = useMemo(() => {
		if (isAuthenticated || !repositoryId) {
			return undefined;
		}
		const repository = allRepositories.find((r) => r.id === repositoryId);
		if (!repository) {
			return [];
		}
		const rows = populateRepositoryEntriesForTable(repository, allRepositoryEntries, allPropertyDefinitions, allEntryProperties);
		let filtered = filterOfflineRowsByArchivedAndDataFilter(rows, includeArchived, resolvedDataFilter);
		if (!useTimeBucketsForSeries && xAxisKey) {
			filtered = [...filtered].sort((a, b) => {
				const av = a.data?.[xAxisKey];
				const bv = b.data?.[xAxisKey];
				const an = Number(av);
				const bn = Number(bv);
				if (Number.isFinite(an) && Number.isFinite(bn)) {
					return an - bn;
				}
				return String(av ?? '').localeCompare(String(bv ?? ''));
			});
		}
		return filtered;
	}, [
		allPropertyDefinitions,
		allRepositories,
		allRepositoryEntries,
		allEntryProperties,
		includeArchived,
		isAuthenticated,
		repositoryId,
		resolvedDataFilter,
		useTimeBucketsForSeries,
		xAxisKey,
	]);
	const repositoryEntries = isAuthenticated ? (data?.rows ?? []) : (offlineRepositoryEntries ?? []);
	const isRepositoryEntriesLoading = Boolean(isAuthenticated && isFetching);

	const chartData = useMemo(
		() => buildCanvasChartSeries({
			rows: repositoryEntries,
			metricKey,
			xAxisKey,
			timeScale: timeScaleForSeries,
			useTimeBuckets: useTimeBucketsForSeries,
			timeBucketRangeFromFilter,
			missingBucketMetric: presentationVariant === 'line' ? 'null' : 'zero',
		}),
		[
			repositoryEntries,
			metricKey,
			xAxisKey,
			timeScaleForSeries,
			useTimeBucketsForSeries,
			timeBucketRangeFromFilter,
			presentationVariant,
		]
	);
	const chartStyle = {
		width: '100%',
		maxWidth: '300px',
		maxHeight: '100px',
		aspectRatio: 1.618,
	} as const;
	const seriesColor = '#8884d8';

	const showBar = presentationVariant === 'bar' && !heatmapMode;
	const chartTarget = useMemo(
		() =>
			resolveChartTargetReference({
				targetA,
				targetB,
				chartData,
			}),
		[targetA, targetB, chartData],
	);
	const targetStroke = theme.palette.warning.main;

	return (
		<CanvasCardFrame
			position={position}
			scale={scale}
			isSelected={isSelected}
			collectionColor={collectionColor}
			isPositionLocked={isPositionLocked}
			onPositionChange={onPositionChange}
			constrainPosition={constrainPosition}
			onDragStart={onDragStart}
			onDragActivate={onDragActivate}
			onDragMove={onDragMove}
			onDragEnd={onDragEnd}
			cardNodeRef={cardNodeRef}
			onFocus={onFocus}
			onBlur={onBlur}
			onSelect={onSelect}
			ariaLabel={label || 'Untitled'}
			maxWidth={true}
		>
			<Box sx={{ width: '100%', minHeight: heatmapMode ? 72 : undefined }}>
				{heatmapMode ? (
					chartData.length > 0 ? (
						<ChartContributionHeatmap series={chartData} range={timeBucketRangeFromFilter} maxWeeks={10} cellGap={2} cellMinSize={3} maxHeight={chartStyle.maxHeight} />
					) : (
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 0.5 }}>
							No data in range
						</Typography>
					)
				) : showBar ? (
					<BarChart style={chartStyle} data={chartData}>
						<XAxis dataKey="xValue" hide />
						<YAxis width="auto" />
						<ChartTargetReferenceLines reference={chartTarget} stroke={targetStroke} />
						<Bar
							dataKey="metricValue"
							fill={seriesColor}
							maxBarSize={48}
							radius={[2, 2, 0, 0]}
							isAnimationActive={false}
						/>
					</BarChart>
				) : (
					<LineChart style={chartStyle} data={chartData}>
						<XAxis dataKey="xValue" hide />
						<YAxis width="auto" />
						<ChartTargetReferenceLines reference={chartTarget} stroke={targetStroke} />
						<Line
							type="monotone"
							dataKey="metricValue"
							stroke={seriesColor}
							strokeWidth={2}
							connectNulls={true}
							activeDot={{ r: 4 }}
							isAnimationActive={false}
						/>
					</LineChart>
				)}
			</Box>
			<Box display="flex" flexDirection="column" alignItems="stretch" gap={0.25}>
				<Typography variant="body2" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word', py: 0.25 }} title={label}>
					{label || 'Untitled'}
				</Typography>
				{isRepositoryEntriesLoading && (
					<Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
						Loading entries...
					</Typography>
				)}
			</Box>
		</CanvasCardFrame>
	);
}

/** Memoized so panning/zooming (which re-renders CanvasView every frame) doesn't also re-render every chart card — CanvasView keeps this card's props referentially stable across those frames specifically so this bail-out holds. */
export const CanvasChartCard = React.memo(CanvasChartCardBase);
export default CanvasChartCard;
